import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkle, PaperPlaneTilt, Plus, TrashSimple, PencilSimple, CheckCircle, Image as ImageIcon,
  CalendarBlank, ArrowClockwise, PushPin, NotePencil, ClockCounterClockwise,
  Paperclip, X, Microphone, FileText, VideoCamera, Check, Copy, ChatsCircle, EnvelopeSimple,
  Lightbulb, FilmSlate, SquaresFour, WaveSine, Stop,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { stripMd, renderRich } from "@/lib/richText";
import { VoicePicker } from "@/components/VoicePicker";
import { VoiceoverPicker } from "@/components/VoiceoverPicker";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRole } from "@/hooks/useRole";
import { Eye } from "@phosphor-icons/react";

const MODEL_KEY = "cd_model";

const formatMsgTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return time;
  return `${d.toLocaleDateString([], { day: "numeric", month: "short" })} · ${time}`;
};

const formatElapsed = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const SUGGESTIONS = [
  "Give me a visual direction for a calming post about our new massage service",
  "Plan a 5-post launch campaign for lymphatic drainage across Instagram and Facebook",
  "Critique what's currently in the approval queue against our brand",
  "Stage an on-brand post with an image of a bright, airy treatment room",
];

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return d; }
};

// Shee writes with **bold** markdown — render it as real bold on screen, and let Copy strip it
// to plain text so pasting elsewhere never carries the chat bubble's background highlight.

function GraphicJobCard({ jobId }) {
  const api = useApi();
  const [status, setStatus] = useState("running");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    const startedAt = Date.now();
    tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    const poll = async () => {
      try {
        const r = await api.get("/admin/graphic-renders/status", { params: { job_id: jobId } });
        if (r.data.status === "done") { setStatus("done"); setResult(r.data); clearInterval(pollRef.current); clearInterval(tickRef.current); }
        else if (r.data.status === "error") { setStatus("error"); setError(r.data.error || "That render failed — try again."); clearInterval(pollRef.current); clearInterval(tickRef.current); }
        else if (r.data.status === "canceled") { setStatus("canceled"); clearInterval(pollRef.current); clearInterval(tickRef.current); }
      } catch { /* transient network hiccup — keep polling */ }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { clearInterval(pollRef.current); clearInterval(tickRef.current); };
  }, [jobId, api]);

  const fmtElapsed = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  if (status === "error") {
    return (
      <div className="bg-[#fdecec] border border-[#f3b8b8] rounded-[10px] p-3" data-testid="cd-action-graphic-job-error">
        <p className="text-[#a13a3a] text-xs font-semibold mb-1">That render failed</p>
        <p className="text-[#a13a3a] text-[11px]">{error}</p>
      </div>
    );
  }
  if (status === "canceled") {
    return <div className="bg-[#f5f5f5] border border-[#e5e5e5] rounded-[10px] p-3" data-testid="cd-action-graphic-job-canceled">
      <p className="text-[#7A9090] text-xs">Render stopped — no image was saved.</p>
    </div>;
  }
  if (status === "done" && result) {
    return (
      <div className="bg-white border border-[#d7e6e9] rounded-[10px] p-2.5" data-testid="cd-action-graphic-rendered">
        {result.data_url && (
          <img src={result.data_url} alt="" className="w-full max-h-72 object-contain bg-[#f5f5f5] rounded-[8px] mb-2 cursor-zoom-in" data-testid="cd-graphic-rendered-thumb" />
        )}
        <p className="text-[#7A9090] text-[11px] mb-1">
          {result.width}×{result.height}px · revision {result.render_count} of {(result.render_count || 0) + (result.renders_remaining || 0)}
        </p>
        <button onClick={() => window.open("/app/media?tab=pending", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-media-graphic">
          <ImageIcon size={14} weight="fill" /> Saved to Media Library — pending review
        </button>
      </div>
    );
  }
  return (
    <div className="bg-[#f0f7f8] border border-[#d7e6e9] rounded-[10px] p-3 flex items-center gap-2.5" data-testid="cd-action-graphic-job-running">
      <span className="w-3.5 h-3.5 rounded-full border-2 border-[#3A7A8C] border-t-transparent animate-spin shrink-0" />
      <p className="text-[#3A7A8C] text-xs">Rendering graphic… {fmtElapsed}</p>
    </div>
  );
}

function VideoJobCard({ jobId, kind }) {
  const api = useApi();
  const [status, setStatus] = useState("running");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    const startedAt = Date.now();
    tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    const poll = async () => {
      try {
        const r = await api.get("/admin/reels/status", { params: { job_id: jobId } });
        if (r.data.status === "done") { setStatus("done"); setResult(r.data); clearInterval(pollRef.current); clearInterval(tickRef.current); }
        else if (r.data.status === "error") { setStatus("error"); setError(r.data.error || "That render failed — try again."); clearInterval(pollRef.current); clearInterval(tickRef.current); }
        else if (r.data.status === "canceled") { setStatus("canceled"); clearInterval(pollRef.current); clearInterval(tickRef.current); }
      } catch { /* transient network hiccup — keep polling */ }
    };
    poll();
    pollRef.current = setInterval(poll, 4000);
    return () => { clearInterval(pollRef.current); clearInterval(tickRef.current); };
  }, [jobId, api]);

  const stop = async () => {
    setStopping(true);
    try {
      await api.post("/admin/reels/cancel", { job_id: jobId });
      setStatus("canceled");
      clearInterval(pollRef.current);
      clearInterval(tickRef.current);
    } catch {
      toast.error("Couldn't stop the render — it may already be finishing.");
    } finally {
      setStopping(false);
    }
  };

  const fmtElapsed = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  if (status === "error") {
    return (
      <div className="bg-[#fdecec] border border-[#f3b8b8] rounded-[10px] p-3" data-testid="cd-action-video-job-error">
        <p className="text-[#a13a3a] text-xs font-semibold mb-1">That render failed</p>
        <p className="text-[#a13a3a] text-[11px]">{error}</p>
      </div>
    );
  }
  if (status === "canceled") {
    return (
      <div className="bg-[#f5f5f5] border border-[#e5e7eb] rounded-[10px] p-3" data-testid="cd-action-video-job-canceled">
        <p className="text-[#7A9090] text-xs font-semibold">Rendering stopped — no finished video was saved.</p>
      </div>
    );
  }
  if (status === "done") {
    const a = result || {};
    return (
      <div className="bg-white border border-[#d7e6e9] rounded-[10px] p-2.5" data-testid="cd-action-video-job-done">
        {a.video_url && <video controls src={a.video_url} className="w-full max-w-[220px] rounded-[10px] bg-black aspect-[9/16] object-cover mb-2" data-testid="cd-video-player" />}
        {a.script && <p className="text-[#3C4048] text-xs mb-2">{renderRich(a.script)}</p>}
        {a.cta && <p className="text-[#7A9090] text-[11px] mb-1.5">{a.cta}</p>}
        <button onClick={() => window.open("/app/media", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-media-video">
          <ImageIcon size={14} weight="fill" /> Saved to Media Library
        </button>
      </div>
    );
  }
  return (
    <div className="bg-[#fff8e6] border border-[#f0d98a] rounded-[10px] p-3" data-testid="cd-action-video-job-running">
      <p className="text-[#8a6d1a] text-xs font-semibold flex items-center gap-1.5 mb-1">
        <VideoCamera size={13} weight="fill" /> Rendering your {kind} reel… {fmtElapsed}
      </p>
      <p className="text-[#8a6d1a] text-[11px] mb-2">Usually a few minutes — I'll drop it in right here when it's ready. Feel free to keep chatting.</p>
      <Button size="sm" variant="outline" onClick={stop} disabled={stopping}
        className="h-7 text-xs border-[#8a6d1a] text-[#8a6d1a] hover:bg-[#8a6d1a] hover:text-white rounded-[8px]"
        data-testid="cd-video-job-stop-btn">
        {stopping ? "Stopping…" : "Stop rendering"}
      </Button>
    </div>
  );
}

function ResolvedAssetCard({ mediaId, displayName, thumbnailUrl, kind, reviewStatus, onViewLibrary }) {
  const api = useApi();
  const [details, setDetails] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!mediaId) return undefined;
    api.get(`/media/${mediaId}/details`).then((r) => { if (alive) setDetails(r.data); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  const statusLabel = reviewStatus === "approved" ? "Approved" : reviewStatus === "rejected" ? "Rejected"
    : reviewStatus === "pending" ? "Pending review" : "Ready";
  const statusTone = reviewStatus === "approved" ? "approved" : reviewStatus === "rejected" ? "rejected" : "pending";

  const download = async () => {
    if (downloading || !mediaId) return;
    setDownloading(true);
    try {
      const r = await api.get(`/media/${mediaId}/download`, { responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const name = match ? match[1] : (displayName || "download");
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: r.headers?.["content-type"] || "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = blobUrl; link.download = name;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Couldn't download — try again");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white border border-[#d7e6e9] rounded-[10px] p-2.5 flex gap-2.5" data-testid="cd-resolved-asset-card">
      {thumbnailUrl && (
        <img src={thumbnailUrl} alt="" onClick={() => setLightboxSrc(thumbnailUrl)}
          className="w-14 h-14 rounded-[8px] object-cover shrink-0 cursor-zoom-in" data-testid="cd-resolved-asset-thumb" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[#3C4048] text-xs font-semibold truncate" data-testid="cd-resolved-asset-name">{displayName || "Selected asset"}</p>
        <p className="text-[#7A9090] text-[10px] mb-1" data-testid="cd-resolved-asset-meta">
          {(kind || "image")}{details?.width ? ` · ${details.width}×${details.height}px` : ""}
        </p>
        <span
          data-testid="cd-resolved-asset-status"
          className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full mb-1.5 ${
            statusTone === "approved" ? "bg-[#e3f1f0] text-[#3A7A8C]" : statusTone === "rejected" ? "bg-[#fbeeee] text-[#b45454]" : "bg-[#fdf1dc] text-[#9a6a1f]"
          }`}
        >
          {statusLabel}
        </span>
        <div className="flex items-center gap-3">
          <button onClick={download} disabled={downloading} data-testid="cd-resolved-asset-download"
            className="text-[#3A7A8C] text-[11px] font-semibold hover:underline disabled:opacity-60">
            {downloading ? "Downloading…" : "Download"}
          </button>
          <button onClick={onViewLibrary} data-testid="cd-resolved-asset-review"
            className="text-[#3A7A8C] text-[11px] font-semibold hover:underline">
            Review in Media Library
          </button>
        </div>
      </div>
      {lightboxSrc && (
        <div onClick={() => setLightboxSrc(null)} className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6 cursor-zoom-out" data-testid="cd-resolved-asset-lightbox">
          <img src={lightboxSrc} alt="" className="max-w-full max-h-full object-contain rounded-[8px]" />
        </div>
      )}
    </div>
  );
}

function ActionCard({ action, onGoApprovals, onContinueRoadmap, onSendMessage, onAttachClick, superseded }) {
  const a = action || {};
  const api = useApi();
  const [reviewStatus, setReviewStatus] = useState(null); // null | "approved" | "rejected"
  const [reviewLoading, setReviewLoading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [chosen, setChosen] = useState(null); // picked stock_photo_options result
  const [choosingIdx, setChoosingIdx] = useState(null);
  const [pickedMediaId, setPickedMediaId] = useState(null); // image_suggestion_options / media_library_options selection
  const [altsRequested, setAltsRequested] = useState(false);
  const [forceExpand, setForceExpand] = useState(false); // re-open a superseded choice card on demand
  const [downloadingFmt, setDownloadingFmt] = useState(null); // newsletter_drafted export in progress
  const [imgApprovalDecision, setImgApprovalDecision] = useState(null); // null | "approved" | "denied"
  const [imgApprovalLoading, setImgApprovalLoading] = useState(false);
  useEffect(() => {
    if (action?.type !== "image_generation_approval_requested" || !action?.request_id) return;
    let cancelled = false;
    api.get(`/creative-director/image-approval/${action.request_id}`).then((res) => {
      if (cancelled) return;
      const status = res?.data?.status;
      if (status === "denied") setImgApprovalDecision("denied");
      else if (status === "approved" || status === "consumed") setImgApprovalDecision("approved");
      // "pending" (or request not found yet) -> leave null so the live Approve/Deny buttons show
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action?.request_id]);
  const isResolved = !!(pickedMediaId || chosen);
  // A choice card is "stale" once a LATER message in this same conversation already resolved
  // SOME image choice (superseded, computed by the parent from message history — not ephemeral
  // local state) and THIS card was never the one actually clicked. Without this, every reload/
  // re-render would show every past picker as if a decision were still outstanding.
  const isStale = superseded && !isResolved && !forceExpand;
  const staleSummary = (label) => (
    <div className={wrap} data-testid="cd-action-superseded-choice">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[#7A9090] text-xs">{label} — resolved earlier in this conversation.</p>
        <button onClick={() => setForceExpand(true)} data-testid="cd-superseded-expand-btn"
          className="text-[#3A7A8C] text-[11px] font-semibold hover:underline shrink-0">Show options</button>
      </div>
    </div>
  );
  const wrap = "bg-white border border-[#d7e6e9] rounded-[10px] p-2.5";
  const resultMediaId = chosen?.media_id || a.media_id;
  const link = (label, Icon) => (
    <button onClick={onGoApprovals} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-approvals">
      <Icon size={14} weight="fill" /> {label}
    </button>
  );
  const thumb = (src, testid, kind) => (
    kind === "video" ? (
      <video src={src} controls className="w-full max-h-56 rounded-[8px] mb-2" data-testid={testid} />
    ) : (
      <img
        src={src} alt="" onClick={() => setLightboxSrc(src)}
        className="w-full max-h-56 object-cover rounded-[8px] mb-2 cursor-zoom-in"
        data-testid={testid}
      />
    )
  );
  const lightbox = lightboxSrc && (
    <div onClick={() => setLightboxSrc(null)} className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6 cursor-zoom-out" data-testid="cd-image-lightbox">
      <img src={lightboxSrc} alt="" className="max-w-full max-h-full object-contain rounded-[8px]" />
      <button onClick={() => setLightboxSrc(null)} className="absolute top-4 right-4 text-white/80 hover:text-white" data-testid="cd-image-lightbox-close">
        <X size={28} />
      </button>
    </div>
  );
  const reviewMedia = async (verdict) => {
    if (!resultMediaId || reviewLoading) return;
    setReviewLoading(true);
    try {
      const routeVerb = verdict === "approved" ? "approve" : "reject";
      await api.post(`/media/${resultMediaId}/${routeVerb}`);
      setReviewStatus(verdict);
      toast.success(verdict === "approved" ? "Approved — now usable in campaigns" : "Rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${verdict === "approved" ? "approve" : "reject"} — try again`);
    } finally {
      setReviewLoading(false);
    }
  };
  const chooseOption = async (opt, idx) => {
    if (choosingIdx !== null) return;
    setChoosingIdx(idx);
    try {
      const r = await api.post("/media/import-stock-photo", {
        source: opt.source, id: opt.id, title: opt.title, query: a.query, download_url: opt.download_url, kind: opt.kind,
      });
      setChosen(r.data);
      onSendMessage?.(`Use this image (media_id: ${r.data.media_id}) for "${a.query}" — just imported it from ${opt.source}, review_status: pending.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that one — try another option");
    } finally {
      setChoosingIdx(null);
    }
  };
  const pickImage = (mediaId, subject) => {
    if (!mediaId || pickedMediaId || !onSendMessage) return;
    setPickedMediaId(mediaId);
    onSendMessage(`Use this image (media_id: ${mediaId})${subject ? ` for ${subject}` : ""}.`);
  };
  const requestAlternatives = (options, subject) => {
    if (altsRequested || !onSendMessage) return;
    setAltsRequested(true);
    const ids = (options || []).map((o) => o.media_id).filter(Boolean).join(", ");
    onSendMessage(`None of these work${subject ? ` for ${subject}` : ""} — show me different options` + (ids ? ` (avoid media_ids: ${ids})` : "") + ".");
  };
  const uploadInstead = () => {
    onAttachClick?.();
    toast.info("Attach your own image below, then send your message.");
  };
  const decideImageApproval = async (requestId, decision) => {
    if (imgApprovalLoading || imgApprovalDecision) return;
    setImgApprovalLoading(true);
    try {
      await api.post(`/creative-director/image-approval/${requestId}/${decision}`);
      setImgApprovalDecision(decision === "approve" ? "approved" : "denied");
      toast.success(decision === "approve" ? "Approved — Shee can now generate this specific image" : "Denied — Shee will not generate this image");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't record your decision — try again");
    } finally {
      setImgApprovalLoading(false);
    }
  };
  const downloadNewsletter = async (newsletterId, fmt) => {
    if (downloadingFmt) return;
    setDownloadingFmt(fmt);
    try {
      const r = await api.get(`/newsletters/${newsletterId}/export.${fmt}`, { responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const name = match ? match[1] : `newsletter.${fmt}`;
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: r.headers?.["content-type"] || "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = blobUrl; link.download = name;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Couldn't download — try again");
    } finally {
      setDownloadingFmt(null);
    }
  };
  const renderStockResult = (dataUrl, sourcePlatform, kind) => (
    <>
      {dataUrl && thumb(dataUrl, "cd-stock-photo-thumb", kind)}
      <p className="text-[#7A9090] text-[11px] mb-1.5 capitalize">Found on {sourcePlatform} — needs your review</p>
      {reviewStatus ? (
        <p className={`text-xs font-semibold capitalize mb-1.5 ${reviewStatus === "approved" ? "text-[#3A7A8C]" : "text-[#b45454]"}`} data-testid="cd-stock-photo-review-status">
          {reviewStatus}
        </p>
      ) : (
        <div className="flex items-center gap-2 mb-1.5">
          <button onClick={() => reviewMedia("approved")} disabled={reviewLoading} data-testid="cd-approve-stock-photo"
            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#5FA8B4] hover:bg-[#3A7A8C] px-2.5 py-1 rounded-full disabled:opacity-60">
            <Check size={13} weight="bold" /> Approve
          </button>
          <button onClick={() => reviewMedia("rejected")} disabled={reviewLoading} data-testid="cd-reject-stock-photo"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#b45454] border border-[#e3b8b8] hover:bg-[#fbeeee] px-2.5 py-1 rounded-full disabled:opacity-60">
            <X size={13} /> Reject
          </button>
        </div>
      )}
      <button onClick={() => window.open("/app/media?tab=pending", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-pending-media">
        <ImageIcon size={14} weight="fill" /> Review in Media Library
      </button>
    </>
  );
  const numberBadge = (n) => (
    <span className="absolute top-1 left-1 z-10 w-5 h-5 rounded-full bg-black/70 text-white text-[11px] font-bold flex items-center justify-center" data-testid={`cd-option-number-${n}`}>
      {n}
    </span>
  );
  const statusPill = (label, tone, testid) => (
    <span
      data-testid={testid}
      className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full mb-1 ${
        tone === "approved" ? "bg-[#e3f1f0] text-[#3A7A8C]" : "bg-[#fdf1dc] text-[#9a6a1f]"
      }`}
    >
      {label}
    </span>
  );
  let content;
  switch (a.type) {
    case "media_library_options":
      if (isStale) { content = staleSummary(`Image choice for "${a.query}"`); break; }
      if (pickedMediaId) {
        const picked = (a.options || []).find((o) => o.media_id === pickedMediaId);
        content = (
          <ResolvedAssetCard
            mediaId={pickedMediaId}
            displayName={picked?.display_name || picked?.filename}
            thumbnailUrl={picked?.thumbnail_url}
            kind={picked?.kind}
            reviewStatus={picked?.review_status}
            onViewLibrary={() => window.open("/app/media", "_blank")}
          />
        );
        break;
      }
      content = (
        <div className={wrap} data-testid="cd-action-media-library-options">
          <p className="text-[#7A9090] text-[11px] mb-2">Found {a.options?.length || 0} existing item(s) for "{a.query}" in the Media Library:</p>
          <div className="grid grid-cols-2 gap-2">
            {(a.options || []).map((opt, oi) => (
              <div key={oi} className="relative rounded-[8px] overflow-hidden border border-[#e5eef0] bg-white" data-testid={`cd-media-library-option-${oi}`}>
                {numberBadge(oi + 1)}
                {opt.kind === "document" ? (
                  <a href={opt.thumbnail_url} target="_blank" rel="noreferrer"
                    className="w-full h-24 flex flex-col items-center justify-center gap-1 bg-[#eef4f5] hover:bg-[#e0eef0]">
                    <FileText size={22} className="text-[#3A7A8C]" weight="fill" />
                    <span className="text-[10px] text-[#3A7A8C] font-semibold">Open PDF</span>
                  </a>
                ) : (
                  <img src={opt.thumbnail_url} alt="" onClick={() => setLightboxSrc(opt.thumbnail_url)}
                    className="w-full h-24 object-cover cursor-zoom-in" />
                )}
                <div className="p-1.5">
                  {statusPill(opt.review_status === "approved" ? "Existing · Approved" : `Existing · ${opt.review_status || "approved"}`, "approved", `cd-media-library-status-${oi}`)}
                  <span className="block text-[10px] text-[#3C4048] truncate" title={opt.filename}>{opt.filename}</span>
                  {opt.tags?.length > 0 && <span className="block text-[10px] text-[#7A9090] truncate">{opt.tags.join(", ")}</span>}
                  {opt.content_preview && <span className="block text-[10px] text-[#7A9090] line-clamp-2 mt-0.5">{opt.content_preview}</span>}
                  {opt.kind !== "document" && (
                    <button onClick={() => pickImage(opt.media_id, `"${a.query}"`)} disabled={!!pickedMediaId}
                      data-testid={`cd-use-media-library-option-${oi}`}
                      className="w-full mt-1.5 text-[11px] font-semibold text-white bg-[#5FA8B4] hover:bg-[#3A7A8C] rounded-full py-1 disabled:opacity-60">
                      {pickedMediaId === opt.media_id ? "✓ Selected" : `Use option ${oi + 1}`}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {!pickedMediaId && (
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => requestAlternatives(a.options, `"${a.query}"`)} disabled={altsRequested}
                data-testid="cd-media-library-show-alternatives"
                className="text-[11px] font-semibold text-[#3A7A8C] hover:underline disabled:opacity-60">
                {altsRequested ? "Requested…" : "Show alternatives"}
              </button>
              <button onClick={uploadInstead} data-testid="cd-media-library-upload-own"
                className="text-[11px] font-semibold text-[#3A7A8C] hover:underline">
                Upload my own
              </button>
            </div>
          )}
          {!pickedMediaId && <p className="text-[#7A9090] text-[11px] mt-2">Or just tell Shee which one to use.</p>}
        </div>
      );
      break;
    case "stock_photo_found":
      content = <div className={wrap} data-testid="cd-action-stock-photo">{renderStockResult(a.data_url, a.source_platform, a.kind)}</div>;
      break;
    case "stock_photo_options":
      if (isStale) { content = staleSummary(`Stock photo choice for "${a.query}"`); break; }
      content = (
        <div className={wrap} data-testid="cd-action-stock-options">
          {chosen ? (
            renderStockResult(chosen.data_url, chosen.source_platform, chosen.kind)
          ) : (
            <>
              <p className="text-[#7A9090] text-[11px] mb-2">Found {a.options?.length || 0} matches for "{a.query}" — pick one:</p>
              <div className="grid grid-cols-2 gap-2">
                {(a.options || []).map((opt, oi) => (
                  <div key={oi} className="relative rounded-[8px] overflow-hidden border border-[#e5eef0] bg-white" data-testid={`cd-stock-option-${oi}`}>
                    {numberBadge(oi + 1)}
                    <img src={opt.thumbnail_url} alt="" onClick={() => setLightboxSrc(opt.thumbnail_url)}
                      className="w-full h-24 object-cover cursor-zoom-in" />
                    <div className="p-1.5">
                      {statusPill(`New · ${opt.source}`, "new", `cd-stock-option-status-${oi}`)}
                      <span className="block text-[9px] text-[#7A9090] mb-1">Not yet in your Library</span>
                      <button onClick={() => chooseOption(opt, oi)} disabled={choosingIdx !== null}
                        data-testid={`cd-choose-stock-option-${oi}`}
                        className="w-full text-[11px] font-semibold text-white bg-[#5FA8B4] hover:bg-[#3A7A8C] rounded-full py-1 disabled:opacity-60">
                        {choosingIdx === oi ? "Adding…" : `Use option ${oi + 1}`}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      );
      break;
    case "image_suggestion_options":
      if (isStale) { content = staleSummary(`Image choice for "${a.prompt}"`); break; }
      if (pickedMediaId) {
        const picked = (a.options || []).find((o) => o.media_id === pickedMediaId);
        content = (
          <ResolvedAssetCard
            mediaId={pickedMediaId}
            displayName="AI-generated image"
            thumbnailUrl={picked?.data_url}
            kind="image"
            reviewStatus="approved"
            onViewLibrary={() => window.open("/app/media", "_blank")}
          />
        );
        break;
      }
      content = (
        <div className={wrap} data-testid="cd-action-image-suggestions">
          <p className="text-[#7A9090] text-[11px] mb-2">{a.options?.length || 0} options for "{a.prompt}" — pick one:</p>
          <div className="grid grid-cols-2 gap-2">
            {(a.options || []).map((opt, oi) => (
              <div key={oi} className="relative rounded-[8px] overflow-hidden border border-[#e5eef0] bg-white" data-testid={`cd-image-suggestion-${oi}`}>
                {numberBadge(oi + 1)}
                <img src={opt.data_url} alt="" onClick={() => setLightboxSrc(opt.data_url)}
                  className="w-full h-24 object-cover cursor-zoom-in" />
                <div className="p-1.5">
                  {statusPill("AI-generated · Ready", "approved", `cd-image-suggestion-status-${oi}`)}
                  <button onClick={() => pickImage(opt.media_id, `"${a.prompt}"`)} disabled={!!pickedMediaId}
                    data-testid={`cd-use-image-suggestion-${oi}`}
                    className="w-full text-[11px] font-semibold text-white bg-[#5FA8B4] hover:bg-[#3A7A8C] rounded-full py-1 disabled:opacity-60">
                    {pickedMediaId === opt.media_id ? "✓ Selected" : `Use option ${oi + 1}`}
                  </button>
                </div>
              </div>
            ))}
          </div>
          {!pickedMediaId && (
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => requestAlternatives(a.options, `"${a.prompt}"`)} disabled={altsRequested}
                data-testid="cd-image-suggestions-show-alternatives"
                className="text-[11px] font-semibold text-[#3A7A8C] hover:underline disabled:opacity-60">
                {altsRequested ? "Requested…" : "Show alternatives"}
              </button>
              <button onClick={uploadInstead} data-testid="cd-image-suggestions-upload-own"
                className="text-[11px] font-semibold text-[#3A7A8C] hover:underline">
                Upload my own
              </button>
            </div>
          )}
          {!pickedMediaId && <p className="text-[#7A9090] text-[11px] mt-2">Or just tell Shee which option to use (e.g. "use option 2").</p>}
        </div>
      );
      break;
    case "tracked_link_created":
      content = (
        <div className={wrap} data-testid="cd-action-tracked-link">
          <p className="text-[#7A9090] text-[11px] mb-1.5">Tracked link for "{a.label}":</p>
          <div className="flex items-center gap-2 bg-white border border-[#e5eef0] rounded-[8px] px-2.5 py-1.5">
            <span className="text-[#3C4048] text-xs truncate flex-1" data-testid="cd-tracked-link-url">{a.short_url}</span>
            <button type="button" data-testid="cd-tracked-link-copy-btn"
              onClick={async () => { try { await navigator.clipboard.writeText(a.short_url); toast.success("Copied"); } catch (e) { toast.error("Couldn't copy"); } }}
              className="text-[#5FA8B4] hover:text-[#3A7A8C] text-xs font-semibold shrink-0">Copy</button>
          </div>
        </div>
      );
      break;
    case "voiceover_generated":
      content = (
        <div className={wrap} data-testid="cd-action-voiceover">
          {a.script && <p className="text-[#3C4048] text-xs mb-2">{renderRich(a.script)}</p>}
          {a.audio_url && <audio controls src={a.audio_url} className="w-full mb-2" data-testid="cd-voiceover-audio" />}
          <p className="text-[#7A9090] text-[11px] mb-1.5 capitalize">Voice: {a.voice_id}</p>
          <button onClick={() => window.open("/app/media", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-media-voiceover">
            <ImageIcon size={14} weight="fill" /> Saved to Media Library
          </button>
        </div>
      );
      break;
    case "video_preview":
      content = (
        <div className="bg-[#fff8e6] border border-[#f0d98a] rounded-[10px] p-3" data-testid="cd-action-video-preview">
          <p className="text-[#8a6d1a] text-xs font-semibold flex items-center gap-1.5 mb-1">
            <VideoCamera size={13} weight="fill" />
            {a.kind === "storyboard" ? `Ready to render a ${a.shot_count}-shot storyboard reel (~${a.total_seconds}s total) — not started yet` : `Ready to render a ${a.kind} reel — not started yet`}
          </p>
          <p className="text-[#8a6d1a] text-[11px]">
            {a.kind === "storyboard"
              ? `${a.shot_count} clips × ~${a.per_shot_seconds}s each, stitched into one reel with one continuous voiceover — takes a few minutes and uses real paid credits per clip.`
              : "Takes a few minutes and uses real paid credits."}
          </p>
          <p className="text-[#8a6d1a] text-[11px] mt-1">Reply "yes" to confirm and render it.</p>
        </div>
      );
      break;
    case "video_generated":
      content = (
        <div className={wrap} data-testid="cd-action-video">
          {a.video_url && <video controls src={a.video_url} className="w-full max-w-[220px] rounded-[10px] bg-black aspect-[9/16] object-cover mb-2" data-testid="cd-video-player" />}
          {a.script && <p className="text-[#3C4048] text-xs mb-2">{renderRich(a.script)}</p>}
          {a.cta && <p className="text-[#7A9090] text-[11px] mb-1.5">{a.cta}</p>}
          <button onClick={() => window.open("/app/media", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-media-video">
            <ImageIcon size={14} weight="fill" /> Saved to Media Library
          </button>
        </div>
      );
      break;
    case "video_job_started":
      content = <VideoJobCard jobId={a.job_id} kind={a.kind} />;
      break;
    case "staged":
      content = (
        <div className={wrap} data-testid="cd-action-staged">
          {a.data_url && thumb(a.data_url, "cd-staged-thumb")}
          {a.preview && <p className="text-[#3C4048] text-xs mb-2 line-clamp-3">{a.preview}</p>}
          {a.scheduled_date && <p className="text-[#7A9090] text-[11px] mb-1.5 flex items-center gap-1"><CalendarBlank size={12} /> {fmtDate(a.scheduled_date)}{a.platforms?.length ? ` · ${a.platforms.join(", ")}` : ""}</p>}
          {link("Staged — review in Approval Queue", CheckCircle)}
        </div>
      );
      break;
    case "campaign_staged":
      content = <div className={wrap} data-testid="cd-action-campaign">{link(`Staged ${a.count} posts — review in Approval Queue`, CalendarBlank)}</div>;
      break;
    case "nuggets_staged":
      content = <div className={wrap} data-testid="cd-action-nuggets">{link(`Staged ${a.count} daily posts — review in Approval Queue`, CalendarBlank)}</div>;
      break;
    case "rewritten":
      content = (
        <div className={wrap} data-testid="cd-action-rewritten">
          <p className="text-[#7A9090] text-[11px] mb-1 flex items-center gap-1"><NotePencil size={12} /> Caption updated</p>
          {a.preview && <p className="text-[#3C4048] text-xs line-clamp-3">{a.preview}</p>}
        </div>
      );
      break;
    case "image_regenerated":
      content = (
        <div className={wrap} data-testid="cd-action-image-regen">
          {a.data_url && thumb(a.data_url, "cd-image-regen-thumb")}
          <p className="text-[#7A9090] text-[11px] flex items-center gap-1"><ArrowClockwise size={12} /> Image regenerated — click to see full image</p>
        </div>
      );
      break;
    case "background_swapped":
      content = (
        <div className={wrap} data-testid="cd-action-background-swapped">
          {a.data_url && (
            <img
              src={a.data_url} alt="" onClick={() => setLightboxSrc(a.data_url)}
              className="w-full max-h-72 object-contain bg-[#f5f5f5] rounded-[8px] mb-2 cursor-zoom-in"
              data-testid="cd-background-swap-thumb"
            />
          )}
          <p className="text-[#7A9090] text-[11px] mb-1.5 flex items-center gap-1"><ImageIcon size={12} /> {a.data_url ? "Background swapped — subject kept exactly as-is, click to see full image" : "Background swapped — subject kept exactly as-is"}</p>
          <button onClick={() => window.open("/app/media", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-media-background-swap">
            <ImageIcon size={14} weight="fill" /> Saved to Media Library
          </button>
        </div>
      );
      break;
    case "rescheduled":
      content = <div className={wrap} data-testid="cd-action-rescheduled"><p className="text-[#3C4048] text-xs flex items-center gap-1"><CalendarBlank size={12} /> Rescheduled to {fmtDate(a.scheduled_date)}</p></div>;
      break;
    case "media_analyzed":
      content = (
        <div className={wrap} data-testid="cd-action-media-analyzed">
          <p className="text-[#7A9090] text-[11px] mb-1.5 flex items-center gap-1"><Eye size={12} weight="fill" /> True Vision{a.kind === "video" ? " — one representative frame" : ""}</p>
          <p className="text-[#3C4048] text-xs whitespace-pre-line">{renderRich(a.analysis)}</p>
          {a.note && <p className="text-[#7A9090] text-[11px] mt-1.5 italic">{a.note}</p>}
        </div>
      );
      break;
    case "shape_previews":
      content = (
        <div className={wrap} data-testid="cd-action-shape-previews">
          <p className="text-[#7A9090] text-[11px] mb-2 flex items-center gap-1"><SquaresFour size={12} weight="fill" /> Shape previews:</p>
          <div className="grid grid-cols-3 gap-2">
            {(a.options || []).map((opt, oi) => (
              <div key={oi} className="rounded-[8px] overflow-hidden border border-[#e5eef0] bg-white" data-testid={`cd-shape-preview-${oi}`}>
                <img src={opt.data_url} alt="" onClick={() => setLightboxSrc(opt.data_url)} className="w-full h-20 object-cover cursor-zoom-in" />
                <span className="block text-[9px] text-[#7A9090] text-center py-1 px-0.5 leading-tight">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
      );
      break;
    case "hooks_generated":
      content = (
        <div className={wrap} data-testid="cd-action-hooks">
          <p className="text-[#7A9090] text-[11px] mb-2 flex items-center gap-1"><Lightbulb size={12} weight="fill" /> {a.hooks?.length || 0} hook options for "{a.topic}":</p>
          <ul className="space-y-1.5">
            {(a.hooks || []).map((h, hi) => (
              <li key={hi} className="text-[#3C4048] text-xs bg-white border border-[#e5eef0] rounded-[8px] px-2.5 py-1.5" data-testid={`cd-hook-${hi}`}>{h}</li>
            ))}
          </ul>
        </div>
      );
      break;
    case "storyboard_built":
      content = (
        <div className={wrap} data-testid="cd-action-storyboard">
          <p className="text-[#3C4048] text-xs font-semibold mb-2 flex items-center gap-1"><FilmSlate size={13} weight="fill" /> {a.title} — {a.shots?.length || 0} shots</p>
          <div className="space-y-2">
            {(a.shots || []).map((s, si) => (
              <div key={si} className="bg-white border border-[#e5eef0] rounded-[8px] p-2" data-testid={`cd-storyboard-shot-${si}`}>
                {s.still_data_url && <img src={s.still_data_url} alt="" onClick={() => setLightboxSrc(s.still_data_url)} className="w-full max-h-32 object-cover rounded-[6px] mb-1.5 cursor-zoom-in" />}
                <p className="text-[10px] text-[#7A9090] font-semibold mb-0.5">Shot {s.shot_number || si + 1}</p>
                <p className="text-[#3C4048] text-xs mb-1">{s.visual_direction}</p>
                {s.vo_line && <p className="text-[#5FA8B4] text-[11px] italic">"{s.vo_line}"</p>}
                {s.on_screen_text && <p className="text-[#7A9090] text-[10px] mt-0.5">On-screen: {s.on_screen_text}</p>}
              </div>
            ))}
          </div>
          <p className="text-[#7A9090] text-[11px] mt-2">Happy with it? Ask me to render it as a real multi-shot reel.</p>
        </div>
      );
      break;
    case "reel_script_rerolled":
      content = (
        <div className={wrap} data-testid="cd-action-reel-script-rerolled">
          <p className="text-[#7A9090] text-[11px] mb-1 flex items-center gap-1"><ArrowClockwise size={12} /> New script</p>
          <p className="text-[#3C4048] text-xs">{renderRich(a.script)}</p>
        </div>
      );
      break;
    case "reel_visual_rerolled":
      content = (
        <div className={wrap} data-testid="cd-action-reel-visual-rerolled">
          <p className="text-[#7A9090] text-[11px] mb-1 flex items-center gap-1"><ArrowClockwise size={12} /> New visual direction</p>
          <p className="text-[#3C4048] text-xs">{a.video_prompt}</p>
        </div>
      );
      break;
    case "graphic_composed":
      content = (
        <div className={wrap} data-testid="cd-action-graphic-composed">
          {a.kind === "video"
            ? (a.video_url && <video controls src={a.video_url} className="w-full max-h-72 rounded-[8px] mb-2" data-testid="cd-composed-video" />)
            : (a.data_url && <img src={a.data_url} alt="" onClick={() => setLightboxSrc(a.data_url)} className="w-full max-h-72 object-contain bg-[#f5f5f5] rounded-[8px] mb-2 cursor-zoom-in" data-testid="cd-composed-thumb" />)}
          <p className="text-[#7A9090] text-[11px] flex items-center gap-1"><ImageIcon size={12} /> Overlay composed onto the Approved asset — usable right away</p>
          <button onClick={() => window.open("/app/media", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-media-composed">
            <ImageIcon size={14} weight="fill" /> Saved to Media Library
          </button>
        </div>
      );
      break;
    case "graphic_render_job_started":
      content = <GraphicJobCard jobId={a.job_id} />;
      break;
    case "graphic_rendered":
      content = (
        <div className={wrap} data-testid="cd-action-graphic-rendered">
          {a.data_url && (
            <img src={a.data_url} alt="" onClick={() => setLightboxSrc(a.data_url)}
              className="w-full max-h-72 object-contain bg-[#f5f5f5] rounded-[8px] mb-2 cursor-zoom-in" data-testid="cd-graphic-rendered-thumb" />
          )}
          <p className="text-[#7A9090] text-[11px] mb-1">
            {a.width}×{a.height}px · revision {a.render_count} of {(a.render_count || 0) + (a.renders_remaining || 0)}
          </p>
          <button onClick={() => window.open("/app/media?tab=pending", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-media-graphic">
            <ImageIcon size={14} weight="fill" /> Saved to Media Library — pending review
          </button>
        </div>
      );
      break;
    case "graphic_inspected":
      content = (
        <div className="bg-[#f0f7f8] border border-[#d7e6e9] rounded-[10px] p-3" data-testid="cd-action-graphic-inspected">
          <p className="text-[#3A7A8C] text-xs font-semibold flex items-center gap-1.5 mb-1.5"><Eye size={13} weight="fill" /> Design critique</p>
          <p className="text-[#52656b] text-[11px] whitespace-pre-line line-clamp-6">{a.critique}</p>
        </div>
      );
      break;
    case "voiceover_attached":
      content = (
        <div className={wrap} data-testid="cd-action-voiceover-attached">
          {a.video_url && <video controls src={a.video_url} className="w-full max-h-72 rounded-[8px] mb-2" data-testid="cd-dubbed-video" />}
          <p className="text-[#7A9090] text-[11px] flex items-center gap-1"><WaveSine size={12} weight="fill" /> Voiceover attached — usable right away</p>
          {a.note && <p className="text-[#7A9090] text-[11px] mt-1 italic">{a.note}</p>}
          <button onClick={() => window.open("/app/media", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline mt-1" data-testid="cd-goto-media-dubbed">
            <ImageIcon size={14} weight="fill" /> Saved to Media Library
          </button>
        </div>
      );
      break;
    case "removed":
      content = <div className={wrap} data-testid="cd-action-removed"><p className="text-[#7A9090] text-xs flex items-center gap-1"><TrashSimple size={12} /> Removed from queue</p></div>;
      break;
    case "brand_update_preview":
      content = (
        <div className="bg-[#fff8e6] border border-[#f0d98a] rounded-[10px] p-3" data-testid="cd-action-brand-preview">
          <p className="text-[#8a6d1a] text-xs font-semibold flex items-center gap-1.5 mb-1"><NotePencil size={13} weight="fill" /> Brand Profile update — preview only, not saved yet</p>
          <p className="text-[#8a6d1a] text-[11px]">Reply "yes" to confirm and save this change.</p>
        </div>
      );
      break;
    case "brand_updated":
      content = (
        <div className={wrap} data-testid="cd-action-brand-updated">
          <p className="text-[#3C4048] text-xs flex items-center gap-1.5 mb-1"><Check size={13} weight="bold" className="text-[#5FA8B4]" /> Brand Profile updated and saved</p>
          <button onClick={() => window.open("/app/brand-assets", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-brand-assets">
            <NotePencil size={14} weight="fill" /> View in Brand Assets
          </button>
        </div>
      );
      break;
    case "image_generation_approval_requested":
      if (imgApprovalDecision || isStale) {
        content = (
          <div className={wrap} data-testid="cd-action-image-approval-decided">
            <p className="text-[#3C4048] text-xs flex items-center gap-1.5">
              {imgApprovalDecision === "approved" ? (
                <><Check size={13} weight="bold" className="text-[#5FA8B4]" /> Approved — Shee can generate this ({a.image_count || 1} image{a.image_count > 1 ? "s" : ""})</>
              ) : imgApprovalDecision === "denied" ? (
                <><X size={13} weight="bold" className="text-[#b45454]" /> Denied — no image will be generated for this</>
              ) : (
                <>Image generation request — resolved earlier in this conversation.</>
              )}
            </p>
          </div>
        );
        break;
      }
      content = (
        <div className="bg-[#fff8e6] border border-[#f0d98a] rounded-[10px] p-3" data-testid="cd-action-image-approval-requested">
          <p className="text-[#8a6d1a] text-xs font-semibold flex items-center gap-1.5 mb-1.5"><Sparkle size={13} weight="fill" /> Shee wants your permission to generate an AI image</p>
          <p className="text-[#8a6d1a] text-[11px] mb-1"><span className="font-semibold">Why:</span> {a.reason}</p>
          <p className="text-[#8a6d1a] text-[11px] mb-1"><span className="font-semibold">Why not existing media:</span> {a.why_existing_media_unsuitable}</p>
          <p className="text-[#8a6d1a] text-[11px] mb-1"><span className="font-semibold">Proposed:</span> {a.proposed_description}</p>
          <p className="text-[#8a6d1a] text-[11px] mb-2">
            <span className="font-semibold">Provider:</span> {a.provider || "fal.ai FLUX"}
            {a.estimated_cost && <> · <span className="font-semibold">Est. cost:</span> {a.estimated_cost}</>}
            {a.image_count > 1 && <> · {a.image_count} images</>}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => decideImageApproval(a.request_id, "approve")} disabled={imgApprovalLoading}
              data-testid="cd-image-approval-approve-btn"
              className="text-[11px] font-semibold text-white bg-[#5FA8B4] hover:bg-[#3A7A8C] rounded-full px-3 py-1 disabled:opacity-60">
              {imgApprovalLoading ? "…" : "Approve"}
            </button>
            <button onClick={() => decideImageApproval(a.request_id, "deny")} disabled={imgApprovalLoading}
              data-testid="cd-image-approval-deny-btn"
              className="text-[11px] font-semibold text-[#8a6d1a] border border-[#f0d98a] hover:bg-[#fff1c7] rounded-full px-3 py-1 disabled:opacity-60">
              Deny
            </button>
          </div>
        </div>
      );
      break;
    case "newsletter_drafted":
      content = (
        <div className={wrap} data-testid="cd-action-newsletter">
          <p className="text-[#3C4048] text-xs font-semibold mb-1">Subject: {a.subject}</p>
          {a.audience_name && <p className="text-[#7A9090] text-[11px] mb-1">Mailchimp audience: {a.audience_name}</p>}
          {a.mailchimp_error && <p className="text-[#b91c1c] text-[11px] mb-1.5">{a.mailchimp_error}</p>}
          {a.preview && <p className="text-[#3C4048] text-xs mb-2 line-clamp-3">{a.preview}</p>}
          {a.links?.length > 0 && (
            <p className="text-[#7A9090] text-[11px] mb-1.5">Links: {a.links.map((l) => l.label).join(", ")}</p>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => window.open("/app/approvals?view=newsletters", "_blank")} className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline" data-testid="cd-goto-newsletters">
              <EnvelopeSimple size={14} weight="fill" /> Drafted — review in Approval Queue
            </button>
            {["html", "png", "pdf"].map((fmt) => (
              <button key={fmt} onClick={() => downloadNewsletter(a.newsletter_id, fmt)} disabled={!!downloadingFmt}
                data-testid={`cd-download-newsletter-${fmt}`}
                className="text-[#3A7A8C] text-[11px] font-semibold hover:underline disabled:opacity-60 uppercase">
                {downloadingFmt === fmt ? "…" : fmt}
              </button>
            ))}
          </div>
        </div>
      );
      break;
    case "handoff":
      content = (
        <div className="bg-[#fff8e6] border border-[#f0d98a] rounded-[10px] p-3" data-testid="cd-action-handoff">
          <p className="text-[#8a6d1a] text-xs font-semibold flex items-center gap-1.5 mb-1"><PushPin size={13} weight="fill" /> Roadmap saved: {a.title}</p>
          <p className="text-[#8a6d1a] text-[11px] mb-2">This chat is getting long. Continue in a fresh, fast conversation with everything preserved.</p>
          <Button size="sm" data-testid="cd-continue-roadmap" onClick={() => onContinueRoadmap(a.roadmap_id)} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[8px] h-8 gap-1.5 text-xs">
            <ArrowClockwise size={13} /> Continue in fresh chat
          </Button>
        </div>
      );
      break;
    default:
      content = null;
  }
  return <>{content}{lightbox}</>;
}

function VoiceButton({ api, onResult, disabled }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mrRef = useRef(null);
  const streamRef = useRef(null);
  const acRef = useRef(null);
  const rafRef = useRef(null);
  const chunksRef = useRef([]);
  const spokeRef = useRef(false);
  const silenceStartRef = useRef(null);

  const cleanup = () => {
    cancelAnimationFrame(rafRef.current);
    if (acRef.current) { acRef.current.close().catch(() => {}); acRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const stop = () => { if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop(); };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      spokeRef.current = false;
      silenceStartRef.current = null;
      const mr = new MediaRecorder(stream);
      mrRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        cleanup();
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1200) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const r = await api.post("/creative-director/transcribe", fd);
          const text = (r.data?.text || "").trim();
          if (text) onResult(text); else toast.error("Didn't catch that — try again");
        } catch (e) { toast.error(e?.response?.data?.detail || "Transcription failed"); }
        finally { setBusy(false); }
      };
      mr.start();
      setRecording(true);
      const AC = window.AudioContext || window.webkitAudioContext;
      const ac = new AC();
      acRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const startedAt = Date.now();
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        if (rms > 0.04) { spokeRef.current = true; silenceStartRef.current = null; }
        else if (spokeRef.current) {
          if (silenceStartRef.current == null) silenceStartRef.current = now;
          else if (now - silenceStartRef.current > 1500) { stop(); return; }
        }
        if (now - startedAt > 30000) { stop(); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      toast.error("Couldn't access the microphone");
      cleanup();
      setRecording(false);
    }
  };

  useEffect(() => () => { stop(); cleanup(); }, []);

  return (
    <Button
      type="button"
      data-testid="cd-mic"
      title={recording ? "Listening… pause to send" : "Speak to Shee"}
      onClick={() => (recording ? stop() : start())}
      disabled={disabled || busy}
      className={`rounded-[12px] h-[52px] w-[52px] p-0 shrink-0 ${recording ? "bg-[#ef4444] hover:bg-[#dc2626] text-white animate-pulse" : "bg-[#eef4f5] hover:bg-[#e0eef0] text-[#3A7A8C]"}`}
    >
      {busy ? <ArrowClockwise size={18} className="animate-spin" /> : <Microphone size={18} weight="fill" />}
    </Button>
  );
}

const LAST_SESSION_KEY = "cd_last_session_id";
const SEEN_MAP_KEY = "cd_seen_sessions";

const readSeenMap = () => { try { return JSON.parse(localStorage.getItem(SEEN_MAP_KEY)) || {}; } catch { return {}; } };
const markSeen = (id) => { const m = readSeenMap(); m[id] = new Date().toISOString(); localStorage.setItem(SEEN_MAP_KEY, JSON.stringify(m)); };
const isUnread = (s) => s.updated_at && new Date(s.updated_at) > new Date(readSeenMap()[s.session_id] || 0);

export default function CreativeDirector() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useRole();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [viewMode, setViewMode] = useState("mine"); // "mine" | "team" (admin only)
  const [viewOwnerId, setViewOwnerId] = useState(null); // set when viewing a teammate's session (read-only)
  const [members, setMembers] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [contextPct, setContextPct] = useState(0);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [voices, setVoices] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [emotionTags, setEmotionTags] = useState([]);
  const [preferredVoiceId, setPreferredVoiceId] = useState("");
  const [preferredDelivery, setPreferredDelivery] = useState("balanced");
  const [preferredPace, setPreferredPace] = useState(1.0);
  const [preferredEmotionTag, setPreferredEmotionTag] = useState(null);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(() => localStorage.getItem(MODEL_KEY) || "openai");
  const [activeJobId, setActiveJobId] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [stage, setStage] = useState(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true); // false once the user scrolls up to read history
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const turnStartRef = useRef(null);
  const textareaRef = useRef(null);

  const startTimer = () => {
    turnStartRef.current = Date.now();
    setElapsedMs(0);
    setStage(null);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - turnStartRef.current), 500);
  };
  const stopTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    setElapsedMs(0);
    setStage(null);
  };

  useEffect(() => {
    api.get("/voices").then((r) => {
      setVoices(r.data.voices || []);
      setDeliveries(r.data.deliveries || []);
      setEmotionTags(r.data.emotion_tags || []);
      setPreferredDelivery(r.data.default_delivery || "balanced");
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/creative-director/models").then((r) => {
      const list = r.data.models || [];
      setModels(list);
      const stored = localStorage.getItem(MODEL_KEY);
      // Claude removed from the picker 2026-10-08 — a stale localStorage value from before the
      // migration (e.g. "claude") must not silently keep running on a now-retired provider.
      if ((!stored || !list.some((m) => m.id === stored)) && r.data.default) {
        setModel(r.data.default);
        localStorage.setItem(MODEL_KEY, r.data.default);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onModelChange = (val) => { setModel(val); localStorage.setItem(MODEL_KEY, val); };

  useEffect(() => {
    if (isAdmin) api.get("/members").then((r) => setMembers(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const memberName = (userId) => members.find((m) => m.user_id === userId)?.name || "Teammate";

  const onAttachClick = () => fileInputRef.current?.click();

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/creative-director/attach", fd);
      setAttachments((a) => [...a, ...(r.data.attachments || [])]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that file");
    } finally { setUploading(false); }
  };

  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get("/creative-director/sessions", { params: viewMode === "team" ? { view_all: true } : {} });
      setSessions(r.data || []);
      return r.data || [];
    } catch (e) { toast.error("Couldn't load conversations"); return []; }
  }, [viewMode]);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  const restoredOnceRef = useRef(false);
  useEffect(() => {
    if (restoredOnceRef.current || viewMode !== "mine") return;
    loadSessions().then((list) => {
      if (restoredOnceRef.current) return;
      restoredOnceRef.current = true;
      // First-ever load on this browser: treat everything already here as "seen" so old
      // conversations don't all light up as unread the moment this feature ships.
      if (localStorage.getItem(SEEN_MAP_KEY) === null) {
        const seed = {};
        list.forEach((s) => { seed[s.session_id] = s.updated_at || new Date().toISOString(); });
        localStorage.setItem(SEEN_MAP_KEY, JSON.stringify(seed));
      }
      const lastId = localStorage.getItem(LAST_SESSION_KEY);
      if (lastId && list.some((s) => s.session_id === lastId)) openSession(lastId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(timerRef.current); }, []);
  useEffect(() => {
    // Only snap to the bottom if the user was already there — never yank them away from history
    // they scrolled up to read (new messages/poll ticks still land, just off-screen until they scroll down).
    if (scrollRef.current && stickToBottomRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);
  const onMessagesScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const openSession = async (id, ownerUserId = null) => {
    setActiveId(id);
    setViewOwnerId(ownerUserId);
    if (!ownerUserId) { localStorage.setItem(LAST_SESSION_KEY, id); markSeen(id); }
    setLoadingSession(true);
    try {
      const params = { session_id: id };
      if (ownerUserId) params.user_id = ownerUserId;
      const r = await api.get(`/creative-director/transcript`, { params });
      stickToBottomRef.current = true; // opening a conversation always starts at the most recent message
      setMessages((r.data.transcript || []).map((t) => ({ role: t.role, content: t.text, actions: t.actions || [], attachments: t.attachments || [], ts: t.ts })));
      setContextPct(r.data.context_pct || 0);
    } catch (e) { toast.error("Couldn't open conversation"); }
    finally { setLoadingSession(false); }
  };

  const newSession = () => {
    const id = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}`;
    setActiveId(id);
    setViewOwnerId(null);
    stickToBottomRef.current = true;
    setMessages([]);
    setContextPct(0);
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await api.post(`/creative-director/reset`, null, { params: { session_id: id } });
      setSessions((s) => s.filter((x) => x.session_id !== id));
      if (activeId === id) { setActiveId(null); setMessages([]); setContextPct(0); }
      if (localStorage.getItem(LAST_SESSION_KEY) === id) localStorage.removeItem(LAST_SESSION_KEY);
    } catch (e) { toast.error("Couldn't delete"); }
  };

  const startRename = (s, e) => {
    e.stopPropagation();
    setRenamingId(s.session_id);
    setRenameValue(s.title || s.preview || "");
  };

  const cancelRename = () => { setRenamingId(null); setRenameValue(""); };

  const submitRename = async (id) => {
    const title = renameValue.trim();
    if (!title) { cancelRename(); return; }
    try {
      await api.patch(`/creative-director/sessions/${id}`, { title });
      setSessions((s) => s.map((x) => (x.session_id === id ? { ...x, title } : x)));
    } catch (e) { toast.error("Couldn't rename conversation"); }
    finally { cancelRename(); }
  };

  // "Use with Shee" / "Redesign with Shee" (Template Gallery) land here with a message to
  // auto-send in a fresh session — runs once, then clears the nav state so a refresh/back
  // doesn't resend it.
  const prefillHandledRef = useRef(false);
  useEffect(() => {
    if (prefillHandledRef.current) return;
    const text = location.state?.prefillMessage;
    if (!text) return;
    prefillHandledRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });
    newSession();
    setTimeout(() => send(text), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const pollJob = (jobId) =>
    new Promise((resolve, reject) => {
      let elapsed = 0;
      let fails = 0;
      pollRef.current = setInterval(async () => {
        elapsed += 1200;
        try {
          const r = await api.get(`/creative-director/chat/status`, { params: { job_id: jobId } });
          fails = 0;
          setStage(r.data.stage || null);
          if (r.data.status === "done" || r.data.status === "canceled") { clearInterval(pollRef.current); resolve(r.data); }
          else if (r.data.status === "error") { clearInterval(pollRef.current); reject(new Error(r.data.error || "The Creative Director hit an error.")); }
          else if (elapsed > 600000) { clearInterval(pollRef.current); reject(new Error("This is taking too long — please try again.")); }
        } catch (e) {
          fails += 1;
          if (fails >= 5 || elapsed > 600000) { clearInterval(pollRef.current); reject(e); }
        }
      }, 1200);
    });

  const interject = async (text) => {
    const msg = (text ?? "").trim();
    if (!msg || !activeJobId) return;
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    setMessages((m) => [...m, { role: "user", content: msg, actions: [], interjected: true, ts: new Date().toISOString() }]);
    try {
      await api.post(`/creative-director/chat/interject`, { job_id: activeJobId, message: msg });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that — Shee may have just finished; try sending it as a new message.");
    }
  };

  const stopTurn = async () => {
    if (!activeJobId) return;
    try {
      const r = await api.post(`/creative-director/chat/stop`, { job_id: activeJobId });
      if (r.data.external_job_may_be_running) {
        toast.info("Stopped — but a video render she'd already started may still finish in the background.");
      }
    } catch (e) {
      toast.error("Couldn't stop — it may have already finished.");
    }
  };

  const send = async (text, roadmapId = null, reviewDecision = null) => {
    if (activeJobId) {
      const msg = (text ?? input).trim();
      if (msg) await interject(msg);
      else toast.info("Shee is still working on your last message — Stop it first if you want to start something new.");
      return;
    }
    const msg = (reviewDecision ? reviewDecision.finalText : (text ?? input)).trim();
    const atts = reviewDecision ? reviewDecision.atts : attachments;
    if ((!msg && atts.length === 0) || sending) return;
    let sid = activeId;
    if (!sid) { sid = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}`; setActiveId(sid); localStorage.setItem(LAST_SESSION_KEY, sid); markSeen(sid); }
    setInput("");
    setAttachments([]);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    stickToBottomRef.current = true; // sending a message always brings you back to the live edge
    const nowTs = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", content: msg, attachments: atts, actions: [], ts: nowTs }, { role: "assistant", content: "", actions: [], streaming: true }]);
    setSending(true);
    startTimer();
    try {
      const payload = {
        message: msg, session_id: sid, roadmap_id: roadmapId, model,
        attachments: atts.map((a) => ({ media_id: a.media_id })),
        preferred_voice_id: preferredVoiceId || undefined,
        preferred_delivery: preferredDelivery || undefined,
        preferred_pace: preferredPace || undefined,
        preferred_emotion_tag: preferredEmotionTag || undefined,
      };
      const start = await api.post(`/creative-director/chat`, payload);
      if (start.data.busy) toast.info("Shee is still finishing your last message — hang tight.");
      setActiveJobId(start.data.job_id);
      const result = await pollJob(start.data.job_id);
      const stopped = result.status === "canceled";
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: result.reply || (stopped ? "Stopped by user." : "Done."), actions: result.actions || [], streaming: false, stopped, ts: result.ts || new Date().toISOString() }; return c; });
      if (typeof result.context_pct === "number") setContextPct(result.context_pct);
      markSeen(sid); // reply landed while this session was open — never show it as unread
      loadSessions();
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: e.message || "Something went wrong. Please try again.", error: true, actions: [], ts: new Date().toISOString() }; return c; });
    } finally { setSending(false); setActiveJobId(null); stopTimer(); }
  };

  const continueRoadmap = (roadmapId) => {
    const id = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}`;
    setActiveId(id);
    stickToBottomRef.current = true;
    setMessages([]);
    setContextPct(0);
    setTimeout(() => send("Let's pick up from the roadmap and keep going.", roadmapId), 50);
  };

  const sessionListEl = (onNavigated) => (
    <>
      <div className="p-3 border-b border-[#e5e7eb] space-y-2">
        <Button data-testid="cd-new-session" onClick={() => { newSession(); onNavigated?.(); }} className="w-full bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[10px] gap-2">
          <Plus size={16} weight="bold" /> New conversation
        </Button>
        {isAdmin && (
          <div className="flex bg-[#eef1f2] rounded-[10px] p-0.5" data-testid="cd-view-toggle">
            <button type="button" onClick={() => { setViewMode("mine"); setActiveId(null); setViewOwnerId(null); setMessages([]); }} data-testid="cd-view-mine"
              className={`flex-1 text-xs font-semibold rounded-[8px] py-1.5 transition-colors ${viewMode === "mine" ? "bg-white text-[#3A7A8C] shadow-sm" : "text-[#7A9090]"}`}>
              My chats
            </button>
            <button type="button" onClick={() => { setViewMode("team"); setActiveId(null); setViewOwnerId(null); setMessages([]); }} data-testid="cd-view-team"
              className={`flex-1 text-xs font-semibold rounded-[8px] py-1.5 transition-colors ${viewMode === "team" ? "bg-white text-[#3A7A8C] shadow-sm" : "text-[#7A9090]"}`}>
              Team
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sessions.length === 0 && <p className="text-[#7A9090] text-sm p-3">{viewMode === "team" ? "No team conversations yet." : "No conversations yet."}</p>}
        {sessions.map((s) => (
          <div
            key={s.session_id + (s.user_id || "")}
            role="button"
            tabIndex={0}
            data-testid={`cd-session-${s.session_id}`}
            onClick={() => { if (renamingId !== s.session_id) { openSession(s.session_id, viewMode === "team" ? s.user_id : null); onNavigated?.(); } }}
            className={`w-full text-left px-3 py-2 rounded-[10px] text-sm flex items-center justify-between group transition-colors cursor-pointer ${activeId === s.session_id ? "bg-[#e6f2f4] text-[#3A7A8C]" : "text-[#3C4048] hover:bg-white"}`}
          >
            {renamingId === s.session_id ? (
              <input
                autoFocus
                data-testid={`cd-rename-input-${s.session_id}`}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); submitRename(s.session_id); }
                  if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                }}
                onBlur={() => submitRename(s.session_id)}
                className="flex-1 min-w-0 bg-white border border-[#5FA8B4] rounded-[6px] px-1.5 py-0.5 text-sm text-[#3C4048] outline-none"
              />
            ) : (
              <span className="truncate flex-1 min-w-0 flex items-center gap-1.5">
                {viewMode === "mine" && s.session_id !== activeId && isUnread(s) && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#3A7A8C] shrink-0" data-testid={`cd-unread-dot-${s.session_id}`} title="Shee replied while you were away" />
                )}
                {viewMode === "team" && <span className="text-[#7A9090] font-semibold">{memberName(s.user_id)}: </span>}
                {s.title || s.preview || "Untitled"}
              </span>
            )}
            {viewMode === "team"
              ? <Eye size={14} className="opacity-0 group-hover:opacity-100 text-[#7A9090] shrink-0 ml-2" />
              : renamingId === s.session_id ? null : (
                <span className="flex items-center gap-1.5 shrink-0 ml-2 opacity-0 group-hover:opacity-100">
                  <PencilSimple size={14} className="text-[#7A9090] hover:text-[#3A7A8C]" onClick={(e) => startRename(s, e)} data-testid={`cd-rename-${s.session_id}`} />
                  <TrashSimple size={15} className="text-[#ef4444]" onClick={(e) => deleteSession(s.session_id, e)} data-testid={`cd-delete-${s.session_id}`} />
                </span>
              )}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="flex gap-6 h-[calc(100vh-3rem)]" data-testid="creative-director">
      {/* Sessions sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[#fafafa] border border-[#e5e7eb] rounded-[12px] overflow-hidden">
        {sessionListEl()}
      </aside>

      {/* Sessions history (mobile/tablet) */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="left" className="p-0 w-[85%] max-w-sm flex flex-col bg-[#fafafa]" data-testid="cd-mobile-history-sheet">
          <SheetHeader className="p-3 border-b border-[#e5e7eb]">
            <SheetTitle className="text-left text-base">Conversations</SheetTitle>
          </SheetHeader>
          <div className="flex-1 flex flex-col overflow-hidden">
            {sessionListEl(() => setHistoryOpen(false))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            data-testid="cd-mobile-history-btn"
            onClick={() => setHistoryOpen(true)}
            className="lg:hidden w-10 h-10 rounded-[12px] bg-[#f0f7f8] flex items-center justify-center text-[#3A7A8C] shrink-0"
            aria-label="Conversations"
          >
            <ChatsCircle size={20} weight="regular" />
          </button>
          <div className="w-10 h-10 rounded-[12px] bg-[#5FA8B4] flex items-center justify-center text-white shrink-0">
            <Sparkle size={22} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-[#3C4048] tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Creative Director</h1>
            {viewOwnerId ? (
              <p className="text-[#a16207] text-sm flex items-center gap-1.5" data-testid="cd-readonly-banner">
                <Eye size={14} weight="fill" /> Viewing {memberName(viewOwnerId)}'s conversation — read-only
              </p>
            ) : (
              <p className="hidden sm:block text-[#7A9090] text-sm">Shee — your on-brand art director. Everything it stages goes to the Approval Queue; nothing publishes on its own.</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:ml-auto">
            {models.length > 0 && (
              <Select value={model} onValueChange={onModelChange}>
                <SelectTrigger data-testid="cd-model-select" className="w-auto h-8 gap-1.5 text-xs font-semibold border-[#e5e7eb] text-[#3A7A8C] shrink-0 rounded-[10px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {models.map((m) => (
                    <SelectItem key={m.id} value={m.id} disabled={!m.ready} data-testid={`cd-model-option-${m.id}`}>
                      {m.label}{!m.ready ? ` (${m.reason || "not configured"})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {activeId && messages.length > 0 && (
              <div className="hidden md:flex items-center gap-2 text-[#7A9090] text-xs shrink-0" title="Conversation memory used" data-testid="cd-context-pct">
                <ClockCounterClockwise size={14} />
                <div className="w-24 h-1.5 rounded-full bg-[#e5e7eb] overflow-hidden">
                  <div className="h-full bg-[#5FA8B4]" style={{ width: `${contextPct}%` }} />
                </div>
                {contextPct}%
              </div>
            )}
          </div>
        </div>

        <div ref={scrollRef} onScroll={onMessagesScroll} className="flex-1 overflow-y-auto bg-white border border-[#e5e7eb] rounded-t-[12px] rounded-b-none md:rounded-[12px] p-5 pb-36 md:pb-5 space-y-4" data-testid="cd-messages">
          {messages.length === 0 && !loadingSession && (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              {/* Mobile: minimal, clean empty state — no placeholder paragraph, no suggestion bubbles */}
              <div className="flex md:hidden flex-col items-center gap-3" data-testid="cd-empty-mobile">
                <div className="w-14 h-14 rounded-full bg-[#eef4f5] flex items-center justify-center text-[#5FA8B4]">
                  <Sparkle size={26} weight="fill" />
                </div>
                <p className="text-[#7A9090] text-sm">Ask Shee anything</p>
              </div>
              {/* Desktop: full intro + suggestions */}
              <div className="hidden md:flex flex-col items-center gap-4">
                <p className="text-[#7A9090] max-w-md">Ask for creative direction, a full campaign, a brand critique, or tell me to stage captions, ads or images.</p>
                <div className="flex flex-col gap-2 w-full max-w-md">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} data-testid={`cd-suggestion-${i}`} onClick={() => send(s)} className="text-left text-sm text-[#3A7A8C] bg-[#f0f7f8] hover:bg-[#e6f2f4] rounded-[10px] px-3 py-2 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => {
          const laterChoiceMade = messages.slice(i + 1).some((mm) => mm.role === "user" && /Use this image \(media_id:/i.test(mm.content || ""));
          return (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`cd-message-${i}`}>
              <div className={`group relative max-w-[80%] rounded-[14px] px-4 py-3 text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#5FA8B4] text-white" : m.error ? "bg-[#fef2f2] text-[#b91c1c] border border-[#fecaca]" : "bg-[#f5f7f7] text-[#3C4048]"}`}>
                {m.role !== "user" && m.content && !m.streaming && (
                  <button
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(stripMd(m.content)); toast.success("Copied — plain text, no highlight"); }
                      catch { toast.error("Couldn't copy — try selecting the text manually"); }
                    }}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-[#7A9090] hover:text-[#3A7A8C] transition-opacity"
                    title="Copy plain text" aria-label="Copy message as plain text" data-testid={`cd-copy-message-${i}`}
                  >
                    <Copy size={14} />
                  </button>
                )}
                {m.content ? renderRich(m.content) : (m.streaming ? (
                  <div className="flex items-center gap-2 text-[#7A9090]" data-testid="cd-working-indicator">
                    <span className="flex gap-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#7A9090] animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#7A9090] animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#7A9090] animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                    <span data-testid="cd-working-stage">{stage || "Working"}</span>
                    <span className="font-mono text-xs" data-testid="cd-working-timer">· {formatElapsed(elapsedMs)}</span>
                    <button type="button" onClick={stopTurn} data-testid="cd-stop-button"
                      className="ml-1 inline-flex items-center gap-1 text-[#b91c1c] hover:text-[#991b1b] font-semibold text-xs border border-[#fecaca] bg-[#fef2f2] rounded-full px-2 py-0.5 transition-colors">
                      <Stop size={11} weight="fill" /> Stop
                    </button>
                  </div>
                ) : "")}
                {m.attachments && m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`cd-msg-attachments-${i}`}>
                    {m.attachments.map((a, k) => (
                      a.kind === "image"
                        ? <img key={k} src={a.file_url} alt={a.filename} className="w-16 h-16 rounded-[8px] object-cover border border-white/30" />
                        : <span key={k} className="inline-flex items-center gap-1 text-[11px] bg-white/20 rounded-[8px] px-2 py-1">
                            {a.kind === "video" ? <VideoCamera size={12} /> : <FileText size={12} />}{a.filename}
                          </span>
                    ))}
                  </div>
                )}
                {m.actions && m.actions.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {m.actions.map((a, j) => (
                      <ActionCard key={j} action={a} onGoApprovals={() => navigate("/approvals")} onContinueRoadmap={continueRoadmap} onSendMessage={send} onAttachClick={onAttachClick} superseded={laterChoiceMade} />
                    ))}
                  </div>
                )}
                {(() => {
                  const sectionTexts = [m.content, ...(m.actions || []).map((a) => a.preview)].filter(Boolean);
                  if (m.role === "user" || m.streaming || sectionTexts.length < 2) return null;
                  return (
                    <button
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(sectionTexts.map(stripMd).join("\n\n")); toast.success("Copied whole reply — plain text"); }
                        catch { toast.error("Couldn't copy — try selecting the text manually"); }
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline"
                      data-testid={`cd-copy-all-${i}`}
                    >
                      <Copy size={13} /> Copy whole reply
                    </button>
                  );
                })()}
                {m.stopped && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-[#92400E] bg-[#FEF3C7] rounded-full px-2 py-0.5" data-testid={`cd-stopped-badge-${i}`}>
                    <Stop size={10} weight="fill" /> Stopped by user
                  </div>
                )}
                {m.ts && !m.streaming && (
                  <div className={`mt-1.5 text-[10px] ${m.role === "user" ? "text-white/60" : "text-[#a8b5b5]"}`} data-testid={`cd-msg-time-${i}`}>
                    {m.interjected && <span className="italic mr-1">(added while Shee was working)</span>}
                    {formatMsgTime(m.ts)}
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>

        <div
          className="fixed md:static inset-x-0 bottom-0 md:inset-auto z-40 bg-white md:bg-transparent border-t md:border-none border-[#e5e7eb] px-4 md:px-0 pt-3 md:pt-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-0 mt-0 md:mt-4"
          data-testid="cd-input-footer"
        >
          {voices.length > 0 && (
            <div className="mb-2" data-testid="cd-voice-picker">
              <VoicePicker
                api={api} voices={voices} deliveries={deliveries} emotionTags={emotionTags}
                voiceId={preferredVoiceId} onVoiceChange={setPreferredVoiceId}
                delivery={preferredDelivery} onDeliveryChange={setPreferredDelivery}
                pace={preferredPace} onPaceChange={setPreferredPace}
                emotionTag={preferredEmotionTag} onEmotionTagChange={setPreferredEmotionTag}
              />
            </div>
          )}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="cd-attachments">
              {attachments.map((a, i) => (
                <div key={i} className="flex items-center gap-2 bg-[#f0f7f8] border border-[#d7e6e9] rounded-[10px] pl-1.5 pr-1 py-1 text-xs text-[#3A7A8C]">
                  {a.kind === "image"
                    ? <img src={a.file_url} alt="" className="w-6 h-6 rounded object-cover" />
                    : (a.kind === "video" ? <VideoCamera size={16} /> : (a.kind === "audio" ? <WaveSine size={16} /> : <FileText size={16} />))}
                  <span className="max-w-[130px] truncate">{a.filename}</span>
                  <button type="button" onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))} data-testid={`cd-remove-attach-${i}`} className="text-[#7A9090] hover:text-[#ef4444] p-0.5">
                    <X size={13} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/markdown,.md,text/html,.html,text/csv,.csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,video/mp4,video/quicktime,video/webm"
              className="hidden"
              onChange={onFilesSelected}
              data-testid="cd-file-input"
            />
            <Button type="button" onClick={onAttachClick} disabled={uploading || sending || !!viewOwnerId} title="Attach image, document or video"
              className="rounded-[12px] h-[52px] w-[52px] p-0 shrink-0 bg-[#eef4f5] hover:bg-[#e0eef0] text-[#3A7A8C]" data-testid="cd-attach">
              {uploading ? <ArrowClockwise size={18} className="animate-spin" /> : <Paperclip size={18} weight="bold" />}
            </Button>
            <VoiceoverPicker api={api} disabled={sending || uploading || !!viewOwnerId}
              onSelect={(item) => setAttachments((a) => [...a, { media_id: item.id, kind: "audio", filename: item.filename || "Voiceover", file_url: item.file_url }])} />
            <VoiceButton api={api} onResult={(t) => send(t)} disabled={sending || uploading || !!viewOwnerId} />
            <Textarea
              ref={textareaRef}
              data-testid="cd-input"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                const el = textareaRef.current;
                if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 240)}px`; }
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.shiftKey) return;
                e.preventDefault();
                send();
              }}
              placeholder={viewOwnerId ? "Read-only — start a new conversation to chat" : (activeJobId ? "Add something while Shee works…" : "Message Shee…")}
              disabled={!!viewOwnerId}
              className="bg-white border-[#e5e7eb] text-[#3C4048] min-h-[52px] max-h-[240px] resize-none rounded-[12px] overflow-y-auto"
            />
            <Button type="submit" disabled={!!viewOwnerId || (!input.trim() && attachments.length === 0) || (sending && !activeJobId)} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] h-[52px] w-[52px] sm:w-auto px-0 sm:px-5 gap-2 shrink-0" data-testid="cd-send">
              <PaperPlaneTilt size={18} weight="fill" /> <span className="hidden sm:inline">{activeJobId ? "Add" : "Send"}</span>
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
